import { useEffect, useMemo, useState } from "react";
import { SectionCard } from "../../../../components/common/section-card/section-card";
import { Heading, Text } from "../../../../components/common/typography";
import { SegmentedControl } from "../../../../components/common/segmented-control/segmented-control";
import { Chip } from "../../../../components/common/chip";
import { Select } from "../../../../components/forms/select";
import { Input } from "../../../../components/common/input";
import { CatalogCrudSection } from "../components/catalog-crud-section";
import { CategoriesPanel } from "../components/categories-panel";
import { useProductWarranties } from "../../../products/hooks/useProductWarranties";
import { useProductWarrantyTypes } from "../../../products/hooks/useProductWarrantyTypes";
import { useProductWarrantyProviders } from "../../../products/hooks/useProductWarrantyProviders";
import {
  attributeValuesCrud,
  attributesCrud,
  brandsCrud,
  conditionsCrud,
  dealTypesCrud,
  resolutionsCrud,
  screenSizesCrud,
  warrantiesCrud,
  warrantyTypesCrud,
  warrantyProvidersCrud,
  warrantyProductsCrud,
  type AttributeRow,
  type WarrantyProductRow,
} from "../../api/admin-catalog-api";

/**
 * Backend curates Odoo's `display_type` enum to these three. Stock Odoo also
 * supports `pills` and `multi`; deliberately omitted until we have a use case.
 * Mirrors `ATTRIBUTE_DISPLAY_TYPES` in
 * procurtal-backend/src/product-attribute/dto/create-product-attribute.dto.ts.
 */
const ATTRIBUTE_DISPLAY_TYPE_OPTIONS = [
  { value: "radio", label: "Radio (one of N — e.g. Storage)" },
  { value: "select", label: "Select (dropdown)" },
  { value: "color", label: "Color (swatches)" },
];

const TABS = [
  { value: "brands", label: "Brands" },
  { value: "categories", label: "Categories" },
  { value: "dealTypes", label: "Deal types" },
  { value: "conditions", label: "Conditions" },
  { value: "screenSizes", label: "Screen sizes" },
  { value: "resolutions", label: "Resolutions" },
] as const;
type TabValue = (typeof TABS)[number]["value"];

const WARRANTY_TABS = [
  { value: "warrantyProducts", label: "Products" },
  { value: "warranties", label: "Periods" },
  { value: "warrantyTypes", label: "Types" },
  { value: "warrantyProviders", label: "Providers" },
] as const;
type WarrantyTabValue = (typeof WARRANTY_TABS)[number]["value"];

function tabLabel(tab: TabValue) {
  return TABS.find((item) => item.value === tab)?.label ?? "Catalog";
}

export function AdminCatalogPage() {
  const [tab, setTab] = useState<TabValue>("brands");
  const [warrantyTab, setWarrantyTab] = useState<WarrantyTabValue>("warrantyProducts");

  return (
    <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-8 py-8 max-md:gap-4 max-md:px-4 max-md:py-6">
      <div className="flex flex-col gap-2">
        <Heading variant="h2" tone="dark">Catalog configuration</Heading>
        <Text variant="bodyLg" tone="muted">
          Manage brands, categories, warranty metadata, and attribute values.
        </Text>
      </div>

      <SectionCard
        shadow
        title="Reference data"
        subtitle="Switch between catalog groups without leaving the page."
        className="gap-6 max-md:gap-5 max-md:p-4"
      >
        <div className="flex flex-col gap-5">
          <div className="overflow-x-auto pb-1">
            <SegmentedControl
              options={TABS}
              value={tab}
              onChange={setTab}
              aria-label="Catalog sections"
              className="min-w-max"
            />
          </div>

          <Text variant="bodySm" tone="muted">
            Editing: {tabLabel(tab)}
          </Text>
        </div>

        {tab === "brands" && (
          <CatalogCrudSection
            title="Brands"
            swrKey="brands"
            fetcher={brandsCrud.list}
            create={brandsCrud.create as never}
            update={brandsCrud.update as never}
            remove={brandsCrud.remove}
          />
        )}
        {tab === "categories" && <CategoriesPanel />}
        {tab === "dealTypes" && (
          <CatalogCrudSection
            title="Deal types"
            swrKey="dealTypes"
            fetcher={dealTypesCrud.list}
            create={dealTypesCrud.create as never}
            update={dealTypesCrud.update as never}
            remove={dealTypesCrud.remove}
          />
        )}
        {tab === "conditions" && (
          <CatalogCrudSection
            title="Conditions"
            swrKey="conditions"
            fetcher={conditionsCrud.list}
            create={conditionsCrud.create as never}
            update={conditionsCrud.update as never}
            remove={conditionsCrud.remove}
          />
        )}
        {tab === "screenSizes" && (
          <CatalogCrudSection
            title="Screen sizes"
            swrKey="screenSizes"
            fetcher={screenSizesCrud.list}
            create={screenSizesCrud.create as never}
            update={screenSizesCrud.update as never}
            remove={screenSizesCrud.remove}
          />
        )}
        {tab === "resolutions" && (
          <CatalogCrudSection
            title="Resolutions"
            swrKey="resolutions"
            fetcher={resolutionsCrud.list}
            create={resolutionsCrud.create as never}
            update={resolutionsCrud.update as never}
            remove={resolutionsCrud.remove}
          />
        )}
      </SectionCard>

      <SectionCard
        shadow
        title="Warranty configuration"
        subtitle="Defaults shown in the seller's product form: warranty periods, types, and providers. These map to the Odoo product.warranty / product.warranty.type / product.warranty.provider models."
        className="gap-6 max-md:gap-5 max-md:p-4"
      >
        <div className="overflow-x-auto pb-1">
          <SegmentedControl
            options={WARRANTY_TABS}
            value={warrantyTab}
            onChange={setWarrantyTab}
            aria-label="Warranty configuration sections"
            className="min-w-max"
          />
        </div>
        {warrantyTab === "warrantyProducts" && <WarrantyProductsPanel />}
        {warrantyTab === "warranties" && (
          <CatalogCrudSection
            title="Warranty periods"
            swrKey="warranties"
            fetcher={warrantiesCrud.list}
            create={warrantiesCrud.create as never}
            update={warrantiesCrud.update as never}
            remove={warrantiesCrud.remove}
          />
        )}
        {warrantyTab === "warrantyTypes" && (
          <CatalogCrudSection
            title="Warranty types"
            swrKey="warrantyTypes"
            fetcher={warrantyTypesCrud.list}
            create={warrantyTypesCrud.create as never}
            update={warrantyTypesCrud.update as never}
            remove={warrantyTypesCrud.remove}
          />
        )}
        {warrantyTab === "warrantyProviders" && (
          <CatalogCrudSection
            title="Warranty providers"
            swrKey="warrantyProviders"
            fetcher={warrantyProvidersCrud.list}
            create={warrantyProvidersCrud.create as never}
            update={warrantyProvidersCrud.update as never}
            remove={warrantyProvidersCrud.remove}
          />
        )}
      </SectionCard>

      <SectionCard
        shadow
        title="Variant attributes"
        subtitle="Curated Odoo attributes that drive variants and storefront filters (Storage, Color, RAM, CPU, …). Add or rename attribute types and their values here."
        className="gap-6 max-md:gap-5 max-md:p-4"
      >
        <AttributesPanel />
      </SectionCard>
    </div>
  );
}

function AttributesPanel() {
  const [attributes, setAttributes] = useState<AttributeRow[] | null>(null);
  const [selectedAttr, setSelectedAttr] = useState<AttributeRow | null>(null);

  const reloadAttrs = async () => {
    const data = await attributesCrud.list();
    setAttributes(data);
    if (selectedAttr) {
      const found = data.find((a) => a.id === selectedAttr.id);
      setSelectedAttr(found ?? null);
    }
  };

  useEffect(() => {
    void reloadAttrs();
  }, []);

  useEffect(() => {
    if (attributes && attributes.length > 0 && !selectedAttr) {
      setSelectedAttr(attributes[0]);
    }
  }, [attributes, selectedAttr]);

  const valuesFetcher = useMemo(() => {
    if (!selectedAttr) return null;
    return () => attributeValuesCrud.listByAttribute(selectedAttr.id);
  }, [selectedAttr]);

  return (
    <div className="flex flex-col gap-6">
      <CatalogCrudSection
        title="Attributes"
        swrKey="attributes"
        fetcher={attributesCrud.list}
        create={attributesCrud.create as never}
        update={attributesCrud.update as never}
        remove={attributesCrud.remove}
        extraFields={[
          {
            key: "displayType",
            label: "Display type",
            render: (r) => (r as unknown as AttributeRow).displayType,
            renderInput: (value, onChange) => (
              <Select
                name="displayType"
                label="Display type"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                options={ATTRIBUTE_DISPLAY_TYPE_OPTIONS}
              />
            ),
            toPayload: (v) => v?.trim() || "radio",
            defaultValue: "radio",
            fromRow: (r) => (r as unknown as AttributeRow).displayType ?? "radio",
          },
        ]}
      />

      {attributes && attributes.length > 0 && (
        <div className="flex flex-col gap-4">
          <Text variant="bodyBold" tone="dark">
            Manage values for:
          </Text>
          <div className="flex flex-wrap gap-2">
            {attributes.map((a) => (
              <Chip
                key={a.id}
                variant="tertiary"
                onClick={() => setSelectedAttr(a)}
                selected={selectedAttr?.id === a.id}
              >
                {a.name}
              </Chip>
            ))}
          </div>

          {selectedAttr && valuesFetcher && (
            <CatalogCrudSection
              key={selectedAttr.id}
              title={`${selectedAttr.name} values`}
              swrKey={`attribute-values-${selectedAttr.id}`}
              fetcher={valuesFetcher}
              create={(input) =>
                attributeValuesCrud.create({
                  name: input.name as string,
                  attributeId: selectedAttr.id,
                })
              }
              update={(id, input) =>
                attributeValuesCrud.update(id, { name: input.name as string })
              }
              remove={attributeValuesCrud.remove}
            />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Admin moderation view for warranty products. Sellers create their own SKUs
 * via /my-warranties → "New warranty" in the create-product form. Admin
 * cannot create here, but can edit (moderation override) and archive
 * (takedown) any seller's warranty.
 */
function WarrantyProductsPanel() {
  const { data: warranties } = useProductWarranties();
  const { data: warrantyTypes } = useProductWarrantyTypes();
  const { data: warrantyProviders } = useProductWarrantyProviders();

  return (
    <div className="flex flex-col gap-3">
      <Text variant="bodySm" tone="muted">
        Read-only audit view. Warranty SKUs are owned by sellers and edited from their product form. Admin can archive a SKU here for moderation.
      </Text>
      <CatalogCrudSection<WarrantyProductRow>
        title="Warranty products"
        swrKey="warrantyProducts"
        fetcher={warrantyProductsCrud.list}
        update={warrantyProductsCrud.update as never}
        remove={warrantyProductsCrud.remove}
        extraFields={[
          {
            key: "price",
            label: "Price (QAR)",
            render: (r) => (r.price != null ? r.price.toLocaleString() : ""),
            renderInput: (value, onChange) => (
              <Input
                label="Price (QAR)"
                type="number"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="e.g. 350"
              />
            ),
            toPayload: (v) => Number(v),
            defaultValue: "",
            fromRow: (r) => (r.price != null ? String(r.price) : ""),
          },
          {
            key: "warrantyId",
            label: "Period",
            render: (r) =>
              (warranties ?? []).find((w) => w.id === r.warrantyId)?.name ?? "",
            renderInput: () => null,
            toPayload: () => undefined,
            defaultValue: "",
            fromRow: (r) => (r.warrantyId != null ? String(r.warrantyId) : ""),
          },
          {
            key: "warrantyTypeId",
            label: "Type",
            render: (r) =>
              (warrantyTypes ?? []).find((t) => t.id === r.warrantyTypeId)?.name ?? "",
            renderInput: () => null,
            toPayload: () => undefined,
            defaultValue: "",
            fromRow: (r) => (r.warrantyTypeId != null ? String(r.warrantyTypeId) : ""),
          },
          {
            key: "warrantyProviderId",
            label: "Provider",
            render: (r) =>
              (warrantyProviders ?? []).find((p) => p.id === r.warrantyProviderId)
                ?.name ?? "",
            renderInput: () => null,
            toPayload: () => undefined,
            defaultValue: "",
            fromRow: (r) =>
              r.warrantyProviderId != null ? String(r.warrantyProviderId) : "",
          },
          {
            key: "sellerCount",
            label: "Sellers",
            render: (r) => String((r.sellerIds ?? []).length),
            renderInput: () => null,
            toPayload: () => undefined,
            defaultValue: "",
            fromRow: () => "",
          },
          {
            key: "description",
            label: "Description",
            render: (r) => r.description ?? "",
            renderInput: (value, onChange) => (
              <Input
                label="Description"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="Buyer-facing terms summary"
              />
            ),
            toPayload: (v) => v?.trim() || undefined,
            defaultValue: "",
            fromRow: (r) => r.description ?? "",
          },
        ]}
      />
    </div>
  );
}

export default AdminCatalogPage;
